import "./LinkifiedText.scss";
import React, { Fragment, ReactNode, useMemo } from "react";
import classNames from "classnames";
import { useHistory } from "react-router";

/**
 * Detects URLs inside plain text:
 *   - `http://` / `https://` URLs
 *   - `www.` URLs
 *   - bare domains with a recognised TLD, optionally followed by a path
 *
 * Kept as a source string and compiled fresh on every use so the stateful
 * `g` flag never leaks `lastIndex` between renders.
 */
const URL_PATTERN_SOURCE =
  "https?://\\S+|www\\.\\S+|\\S+\\.(?:com|org|net|io|fm|co|uk|app)\\S*";

/** Punctuation that should not be swallowed into the end of a link. */
const TRAILING_PUNCTUATION = /[.,!?;:)\]}]+$/;

/** Hosts that resolve to the Highnote web app and should route in-app. */
const HIGHNOTE_APP_HOSTS = ["on.highnote.fm"];

/** Prepends `https://` to a scheme-less URL so it can be navigated to. */
const normalizeHref = (raw: string): string =>
  /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;

/** Splits any trailing punctuation off the end of a detected URL. */
const splitTrailingPunctuation = (
  raw: string,
): { url: string; trailing: string } => {
  const match = raw.match(TRAILING_PUNCTUATION);
  const trailing = match ? match[0] : "";
  return { url: raw.slice(0, raw.length - trailing.length), trailing };
};

/** Parses a (possibly scheme-less) URL, returning null when it is invalid. */
const parseUrl = (raw: string): URL | null => {
  try {
    return new URL(normalizeHref(raw));
  } catch {
    return null;
  }
};

/** True when a URL points back at the Highnote web app. */
const isHighnoteAppUrl = (url: URL): boolean =>
  url.hostname === window.location.hostname ||
  HIGHNOTE_APP_HOSTS.includes(url.hostname);

export type LinkifiedTextProps = {
  /** Untrusted, user-authored text that may contain URLs. */
  text: string;
  /** Optional extra className merged onto the container. */
  className?: string;
};

/**
 * Renders untrusted text, turning any URLs it contains into clickable
 * links. Links are built from parsed string segments and rendered as
 * React elements — never via `dangerouslySetInnerHTML` — so comment text
 * can never inject markup.
 *
 * Links that point back at the Highnote web app (e.g. a space share link)
 * are routed through the client-side router so the user stays in the SPA
 * instead of triggering a full-page load.
 */
export const LinkifiedText: React.FC<LinkifiedTextProps> = ({
  text,
  className,
}) => {
  const history = useHistory();

  const nodes = useMemo<ReactNode[]>(() => {
    if (!text) return [];

    const out: ReactNode[] = [];
    const pattern = new RegExp(URL_PATTERN_SOURCE, "gi");
    let lastEnd = 0;
    let key = 0;
    let match: RegExpExecArray | null;

    while ((match = pattern.exec(text)) !== null) {
      const raw = match[0];
      const start = match.index;

      // Emit the plain text that precedes this match.
      if (start > lastEnd) {
        out.push(<Fragment key={key++}>{text.slice(lastEnd, start)}</Fragment>);
      }
      lastEnd = start + raw.length;

      const { url, trailing } = splitTrailingPunctuation(raw);
      const parsed = url ? parseUrl(url) : null;

      if (!parsed) {
        // URL-like, but not a valid URL — render it verbatim, not clickable.
        out.push(<Fragment key={key++}>{raw}</Fragment>);
        continue;
      }

      const inApp = isHighnoteAppUrl(parsed);
      out.push(
        <a
          key={key++}
          href={normalizeHref(url)}
          className="highnote-linkified-text__link"
          {...(inApp ? {} : { target: "_blank", rel: "noopener noreferrer" })}
          onClick={(e) => {
            // Never let a link click also trigger the surrounding
            // comment card / chat bubble click handler.
            e.stopPropagation();
            if (inApp) {
              // Stay in the SPA instead of doing a full-page reload.
              e.preventDefault();
              history.push(parsed.pathname + parsed.search + parsed.hash);
            }
          }}
        >
          {url}
        </a>,
      );

      // Trailing punctuation renders as ordinary text after the link.
      if (trailing) {
        out.push(<Fragment key={key++}>{trailing}</Fragment>);
      }

      // Defensive guard against a zero-length match looping forever.
      if (pattern.lastIndex === start) pattern.lastIndex++;
    }

    if (lastEnd < text.length) {
      out.push(<Fragment key={key++}>{text.slice(lastEnd)}</Fragment>);
    }

    return out;
  }, [text, history]);

  return (
    <span
      className={classNames("highnote-linkified-text", className)}
      dir="auto"
    >
      {nodes}
    </span>
  );
};
